/**
 * Created by Workaxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev@workaxis.in
 * Date: 19/02/24
 * Time: 4:29 pm
 * Project: W-CRM
 */
import axios from "axios";
import {createSlice, current} from "@reduxjs/toolkit"
import apiService from "../../api/apiService";
import {formatAxiosErrorMessage} from "../../utils/common";
import {endPoints} from "../../api/endpoints";
import {apiUrlBuilder, removeDuplicates} from "../../utils/util";
import {sendWhatsappMessageOverSocket} from "../../api/realtimeSocket";

// When a lead is converted to a contact the API can return both records (same
// mobile, different type_id). Keep the contact ('client') entry and discard the
// lead entry for the same phone number.
const deduplicateClientsByMobile = (clients) => {
    const byMobile = {};
    const noMobile = [];
    for (const item of clients) {
        const mobile = item.mobile;
        if (!mobile) {
            noMobile.push(item);
            continue;
        }
        if (!byMobile[mobile] || (item.type === 'client' && byMobile[mobile].type === 'lead')) {
            byMobile[mobile] = item;
        }
    }
    return [...Object.values(byMobile), ...noMobile];
};

const WhatsappIntegrationInitialState = {
    listMessages: {
        isLoading: false,
        data: null,
        error: null,
    },
    searchMessage: {
        data: null
    },
    sendMessage: {
        isLoading: false,
        data: null,
        error: null,
    },
    templates: {
        isLoading: false,
        data: null,
        error: null,
    },
    integrated: {
        isLoading: false,
        data: null,
        error: null,
    },
    configuration: {
        isLoading: false,
        data: null,
        error: null,
    },
    createConfiguration: {
        isLoading: false,
        data: null,
        error: null,
    },
    deleteConfiguration: {
        isLoading: false,
        data: null,
        error: null,
    },
    updateConfiguration: {
        isLoading: false,
        data: null,
        error: null,
    },
    analytics: {
        isLoading: false,
        data: null,
        error: null,
    },
    message_stats: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulk: {
        isLoading: false,
        data: null,
        error: null,
    },
    unidentified_sender: {
        isLoading: false,
        data: null,
        error: null,
    },
    unidentified_email_sender: {
        isLoading: false,
        data: null,
        error: null,
    },
    delete_unidentified_sender: {
        isLoading: false,
        data: null,
        error: null,
    },
    create_template: {
        isLoading: false,
        data: null,
        error: null,
    },
    delete_template: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulk_status: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkBatches: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkBatchDetail: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkBatchRecipientsDownload: {
        isLoading: false,
        data: null,
        error: null,
    },
    updateBulkBatchStatus: {
        isLoading: false,
        data: null,
        error: null,
    },
    // Scheduled / recurring WhatsApp bulk campaigns
    bulkSchedules: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkScheduleDetail: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkScheduleMutate: {
        isLoading: false,
        data: null,
        error: null,
    },
    create_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    list_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    list_all_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    delete_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    fetch_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    update_chatbot: {
        isLoading: false,
        data: null,
        error: null,
    },
    update_chatbot_status: {
        isLoading: false,
        data: null,
        error: null,
    },
    chat_list: {
        isLoading: false,
        data: null,
        error: null,
    },
    // Agent takeover (live session) for the open WhatsApp thread
    liveSession: {
        isLoading: false,
        data: null,
        error: null,
    },
    // Currently open WhatsApp thread (for global socket routing)
    activeChat: {
        clientId: null,
        type: null,
    },
}

export const WhatsappSlice = createSlice({
    name: "whatsappintegration",
    initialState: {
        ...WhatsappIntegrationInitialState
    }, reducers: {
        ListWhatsappMessageRequesting: (state, action) => {
            state.listMessages.isLoading = true
        },
        ListWhatsappMessageSuccess: (state, action) => {
            const {listMessages} = current(state);
            const newData = action.payload.data;

            if (listMessages?.data?.results) {
                let processed = removeDuplicates([
                    ...listMessages.data.results,
                    ...newData.results,
                ]);
                const sortedList = processed.sort((a, b) => {
                    return new Date(b.created_on) - new Date(a.created_on);
                });
                newData.results = sortedList
            }

            state.listMessages.isLoading = false
            state.listMessages.error = null
            // state.listMessages.data = action.payload.data
            state.listMessages.data = newData;
        },


        WhatsappMessageIncoming: (state, action) => {
            // Accept both legacy { data: message } and typed { type, data: message }
            const newMessage = action.payload?.data ?? action.payload;
            if (!newMessage || typeof newMessage !== "object") {
                return;
            }
            if (!state.listMessages?.data?.results) {
                // Seed list so optimistic send works before first page fetch settles
                state.listMessages.data = {
                    count: 1,
                    next: null,
                    previous: null,
                    results: [newMessage],
                };
                return;
            }

            let updatedResults = [...state.listMessages.data.results];
            // Match by real id, or by fe_reference_id for optimistic reconcile
            const existingIndex = updatedResults.findIndex((message) => {
                if (newMessage.id && message.id === newMessage.id) return true;
                if (
                    newMessage.fe_reference_id &&
                    message.fe_reference_id === newMessage.fe_reference_id
                ) {
                    return true;
                }
                if (
                    newMessage.message_id &&
                    message.message_id === newMessage.message_id
                ) {
                    return true;
                }
                return false;
            });
            if (existingIndex !== -1) {
                updatedResults[existingIndex] = {
                    ...updatedResults[existingIndex],
                    ...newMessage,
                };
            } else {
                updatedResults = removeDuplicates([...updatedResults, newMessage]);
            }
            updatedResults.sort(
                (a, b) => new Date(b.created_on) - new Date(a.created_on)
            );
            state.listMessages.data.results = updatedResults;
        },

        // Paint a pending bubble immediately (optimistic send)
        WhatsappMessageOptimistic: (state, action) => {
            const newMessage = action.payload;
            if (!newMessage?.fe_reference_id) return;
            if (!state.listMessages?.data) {
                state.listMessages.data = {
                    count: 1,
                    next: null,
                    previous: null,
                    results: [newMessage],
                };
                return;
            }
            const results = state.listMessages.data.results || [];
            const exists = results.some(
                (m) => m.fe_reference_id === newMessage.fe_reference_id
            );
            if (!exists) {
                state.listMessages.data.results = [newMessage, ...results].sort(
                    (a, b) => new Date(b.created_on) - new Date(a.created_on)
                );
            }
        },

        WhatsappMessageOptimisticFailed: (state, action) => {
            const refId = action.payload?.fe_reference_id;
            if (!refId || !state.listMessages?.data?.results) return;
            state.listMessages.data.results = state.listMessages.data.results.map(
                (m) =>
                    m.fe_reference_id === refId
                        ? {...m, status: "failed", send_error: true}
                        : m
            );
        },

        SetActiveWhatsappChat: (state, action) => {
            state.activeChat = {
                clientId: action.payload?.clientId ?? null,
                type: action.payload?.type ?? null,
            };
        },

        ClearActiveWhatsappChat: (state) => {
            state.activeChat = {clientId: null, type: null};
        },


        SearchWhatsappMessageSuccess: (state, action) => {
            state.searchMessage.data = action.payload.data
            state.listMessages.isLoading = false
            state.listMessages.error = null

            const {listMessages} = current(state);

            const newData = action.payload.data;

            if (listMessages?.data?.results) {
                newData.results = removeDuplicates([...listMessages.data.results, ...newData.results,]);
                newData.count = listMessages.data.count
            }
            state.listMessages.data = newData;
        },

        ListWhatsappMessageError: (state, action) => {
            state.listMessages.isLoading = false
            state.listMessages.error = action.payload
        },
        ListWhatsappMessageReset: (state, action) => {
            state.listMessages = WhatsappIntegrationInitialState.listMessages
        },


        SearchWhatsappMessageReset: (state, action) => {
            state.searchMessage = WhatsappIntegrationInitialState.searchMessage
        },

        SendWhatsappMessageRequesting: (state, action) => {
            state.sendMessage.isLoading = true
        },
        SendWhatsappMessageSuccess: (state, action) => {
            state.sendMessage.isLoading = false
            state.sendMessage.error = null
            state.sendMessage.data = action.payload.data
        },
        SendWhatsappMessageError: (state, action) => {
            state.sendMessage.isLoading = false
            state.sendMessage.error = action.payload
        },
        SendWhatsappMessageReset: (state, action) => {
            state.sendMessage = WhatsappIntegrationInitialState.sendMessage
        },

        WhatsappTemplatesRequesting: (state, action) => {
            state.templates.isLoading = true
        },
        WhatsappTemplatesSuccess: (state, action) => {
            state.templates.isLoading = false
            state.templates.error = null
            state.templates.data = action.payload.data
        },
        WhatsappTemplatesError: (state, action) => {
            state.templates.isLoading = false
            state.templates.error = action.payload
        },
        WhatsappTemplatesReset: (state, action) => {
            state.templates = WhatsappIntegrationInitialState.templates
        },


        WhatsappIntegratedRequesting: (state, action) => {
            state.integrated.isLoading = true
        },
        WhatsappIntegratedSuccess: (state, action) => {
            state.integrated.isLoading = false
            state.integrated.error = null
            state.integrated.data = action.payload.data
        },
        WhatsappIntegratedError: (state, action) => {
            state.integrated.isLoading = false
            state.integrated.error = action.payload
        },
        WhatsappIntegratedReset: (state, action) => {
            state.integrated = WhatsappIntegrationInitialState.integrated
        },

        WhatsappConfigurationRequesting: (state, action) => {
            state.configuration.isLoading = true
        },
        WhatsappConfigurationSuccess: (state, action) => {
            state.configuration.isLoading = false
            state.configuration.error = null
            state.configuration.data = action.payload.data
        },
        WhatsappConfigurationError: (state, action) => {
            state.configuration.isLoading = false
            state.configuration.error = action.payload
        },
        WhatsappConfigurationReset: (state, action) => {
            state.configuration = WhatsappIntegrationInitialState.configuration
        },

        WhatsappCreateConfigurationRequesting: (state, action) => {
            state.createConfiguration.isLoading = true
        },
        WhatsappCreateConfigurationSuccess: (state, action) => {
            state.createConfiguration.isLoading = false
            state.createConfiguration.error = null
            state.createConfiguration.data = action.payload.data
        },
        WhatsappCreateConfigurationError: (state, action) => {
            state.createConfiguration.isLoading = false
            state.createConfiguration.error = action.payload
        },
        WhatsappCreateConfigurationReset: (state, action) => {
            state.createConfiguration = WhatsappIntegrationInitialState.createConfiguration
        },

        WhatsappDeleteConfigurationRequesting: (state, action) => {
            state.deleteConfiguration.isLoading = true
        },
        WhatsappDeleteConfigurationSuccess: (state, action) => {
            state.deleteConfiguration.isLoading = false
            state.deleteConfiguration.error = null
            state.deleteConfiguration.data = "deleted"
        },
        WhatsappDeleteConfigurationError: (state, action) => {
            state.deleteConfiguration.isLoading = false
            state.deleteConfiguration.data = null
            state.deleteConfiguration.error = action.payload
        },
        WhatsappDeleteConfigurationReset: (state, action) => {
            state.deleteConfiguration = WhatsappIntegrationInitialState.deleteConfiguration
        },

        WhatsappUpdateConfigurationRequesting: (state, action) => {
            state.updateConfiguration.isLoading = true
        },
        WhatsappUpdateConfigurationSuccess: (state, action) => {
            state.updateConfiguration.isLoading = false
            state.updateConfiguration.error = null
            state.updateConfiguration.data = action.payload.data
        },
        WhatsappUpdateConfigurationError: (state, action) => {
            state.updateConfiguration.isLoading = false
            state.updateConfiguration.error = action.payload
        },
        WhatsappUpdateConfigurationReset: (state, action) => {
            state.updateConfiguration = WhatsappIntegrationInitialState.updateConfiguration
        },


        WhatsappAnalyticsRequesting: (state, action) => {
            state.analytics.isLoading = true
        },
        WhatsappAnalyticsSuccess: (state, action) => {
            state.analytics.isLoading = false
            state.analytics.error = null
            state.analytics.data = action.payload.data
        },
        WhatsappAnalyticsError: (state, action) => {
            state.analytics.data = null
            state.analytics.isLoading = false
            state.analytics.error = action.payload
        },
        WhatsappAnalyticsReset: (state, action) => {
            state.analytics = WhatsappIntegrationInitialState.analytics
        },

        MessageStatRequesting: (state, action) => {
            state.message_stats.isLoading = true
        },
        MessageStatSuccess: (state, action) => {
            state.message_stats.isLoading = false
            state.message_stats.error = null
            state.message_stats.data = action.payload.data
        },
        MessageStatError: (state, action) => {
            state.message_stats.data = null
            state.message_stats.isLoading = false
            state.message_stats.error = action.payload
        },
        MessageStatReset: (state, action) => {
            state.message_stats = WhatsappIntegrationInitialState.message_stats
        },


        WhatsappBulkRequesting: (state, action) => {
            state.bulk.isLoading = true
        },
        WhatsappBulkSuccess: (state, action) => {
            state.bulk.isLoading = false
            state.bulk.error = null
            state.bulk.data = action.payload.data
        },
        WhatsappBulkError: (state, action) => {
            state.bulk.data = null
            state.bulk.isLoading = false
            state.bulk.error = action.payload
        },
        WhatsappBulkReset: (state, action) => {
            state.bulk = WhatsappIntegrationInitialState.bulk
        },


        WhatsappUnidentifiedSenderRequesting: (state, action) => {
            state.unidentified_sender.isLoading = true
        },
        WhatsappUnidentifiedSenderSuccess: (state, action) => {
            state.unidentified_sender.isLoading = false
            state.unidentified_sender.error = null
            state.unidentified_sender.data = action.payload.data
        },
        WhatsappUnidentifiedSenderError: (state, action) => {
            state.unidentified_sender.data = null
            state.unidentified_sender.isLoading = false
            state.unidentified_sender.error = action.payload
        },
        WhatsappUnidentifiedSenderReset: (state, action) => {
            state.unidentified_sender = WhatsappIntegrationInitialState.unidentified_sender
        },
        WhatsappUnidentifiedSenderMarkASRead: (state, action) => {
            const id = action.payload;
            if (id) {
                const {unidentified_sender} = current(state);
                const {data} = unidentified_sender;
                const updatedResults = data?.results?.map(item =>
                    item.id === id ? {...item, is_unread_messages: false} : item
                );
                state.unidentified_sender.data = {...data, results: updatedResults};
            }
        },


        EmailUnidentifiedSenderRequesting: (state, action) => {
            state.unidentified_email_sender.isLoading = true
        },
        EmailUnidentifiedSenderSuccess: (state, action) => {
            state.unidentified_email_sender.isLoading = false
            state.unidentified_email_sender.error = null
            state.unidentified_email_sender.data = action.payload.data
        },
        EmailUnidentifiedSenderError: (state, action) => {
            state.unidentified_email_sender.data = null
            state.unidentified_email_sender.isLoading = false
            state.unidentified_email_sender.error = action.payload
        },
        EmailUnidentifiedSenderReset: (state, action) => {
            state.unidentified_email_sender = WhatsappIntegrationInitialState.unidentified_email_sender
        },
        EmailUnidentifiedSenderMarkASRead: (state, action) => {
            const id = action.payload;
            if (id) {
                const {unidentified_sender} = current(state);
                const {data} = unidentified_sender;
                const updatedResults = data.results.map(item =>
                    item.id === id ? {...item, is_unread_messages: false} : item
                );
                state.unidentified_sender.data = {...data, results: updatedResults};
            }
        },


        DeleteWhatsappUnidentifiedSenderRequesting: (state, action) => {
            state.delete_unidentified_sender.isLoading = true
        },
        DeleteWhatsappUnidentifiedSenderSuccess: (state, action) => {
            state.delete_unidentified_sender.isLoading = false
            state.delete_unidentified_sender.error = null
            state.delete_unidentified_sender.data = "deleted"
        },
        DeleteWhatsappUnidentifiedSenderError: (state, action) => {
            state.delete_unidentified_sender.data = null
            state.delete_unidentified_sender.isLoading = false
            state.delete_unidentified_sender.error = action.payload
        },
        DeleteWhatsappUnidentifiedSenderReset: (state, action) => {
            state.delete_unidentified_sender = WhatsappIntegrationInitialState.delete_unidentified_sender
        },

        WhatsappCreateTemplateRequesting: (state, action) => {
            state.create_template.isLoading = true
        },
        WhatsappCreateTemplateSuccess: (state, action) => {
            state.create_template.isLoading = false
            state.create_template.error = null
            state.create_template.data = action.payload.data
        },
        WhatsappCreateTemplateError: (state, action) => {
            state.create_template.data = null
            state.create_template.isLoading = false
            state.create_template.error = action.payload
        },
        WhatsappCreateTemplateReset: (state, action) => {
            state.create_template = WhatsappIntegrationInitialState.create_template
        },

        WhatsappDeleteTemplateRequesting: (state, action) => {
            state.delete_template.isLoading = true
        },
        WhatsappDeleteTemplateSuccess: (state, action) => {
            state.delete_template.isLoading = false
            state.delete_template.error = null
            state.delete_template.data = "deleted"
        },
        WhatsappDeleteTemplateError: (state, action) => {
            state.delete_template.data = null
            state.delete_template.isLoading = false
            state.delete_template.error = action.payload
        },
        WhatsappDeleteTemplateReset: (state, action) => {
            state.delete_template = WhatsappIntegrationInitialState.delete_template
        },


        WhatsappBulkStatusRequesting: (state, action) => {
            state.bulk_status.isLoading = true
        },
        WhatsappBulkStatusSuccess: (state, action) => {
            state.bulk_status.isLoading = false
            state.bulk_status.error = null
            state.bulk_status.data = action.payload.data
        },
        WhatsappBulkStatusError: (state, action) => {
            state.bulk_status.data = null
            state.bulk_status.isLoading = false
            state.bulk_status.error = action.payload
        },
        WhatsappBulkStatusReset: (state, action) => {
            state.bulk_status = WhatsappIntegrationInitialState.bulk_status
        },


        CreateChatBotRequesting: (state, action) => {
            state.create_chatbot.isLoading = true
        },
        CreateChatBotSuccess: (state, action) => {
            state.create_chatbot.isLoading = false
            state.create_chatbot.error = null
            state.create_chatbot.data = action.payload.data
        },
        CreateChatBotError: (state, action) => {
            state.create_chatbot.data = null
            state.create_chatbot.isLoading = false
            state.create_chatbot.error = action.payload
        },
        CreateChatBotReset: (state, action) => {
            state.create_chatbot = WhatsappIntegrationInitialState.create_chatbot
        },

        ListChatBotRequesting: (state, action) => {
            state.list_chatbot.isLoading = true
        },
        ListChatBotSuccess: (state, action) => {
            state.list_chatbot.isLoading = false
            state.list_chatbot.error = null
            state.list_chatbot.data = action.payload.data
        },
        ListChatBotError: (state, action) => {
            state.list_chatbot.data = null
            state.list_chatbot.isLoading = false
            state.list_chatbot.error = action.payload
        },
        ListChatBotReset: (state, action) => {
            state.list_chatbot = WhatsappIntegrationInitialState.list_chatbot
        },

        ListAllChatBotRequesting: (state) => {
            state.list_all_chatbot.isLoading = true
        },
        ListAllChatBotSuccess: (state, action) => {
            state.list_all_chatbot.isLoading = false
            state.list_all_chatbot.error = null
            state.list_all_chatbot.data = action.payload.data
        },
        ListAllChatBotError: (state, action) => {
            state.list_all_chatbot.data = null
            state.list_all_chatbot.isLoading = false
            state.list_all_chatbot.error = action.payload
        },

        DeleteChatBotRequesting: (state, action) => {
            state.delete_chatbot.isLoading = true
        },
        DeleteChatBotSuccess: (state, action) => {
            state.delete_chatbot.isLoading = false
            state.delete_chatbot.error = null
            state.delete_chatbot.data = "ok"
        },
        DeleteChatBotError: (state, action) => {
            state.delete_chatbot.data = null
            state.delete_chatbot.isLoading = false
            state.delete_chatbot.error = action.payload
        },
        DeleteChatBotReset: (state, action) => {
            state.delete_chatbot = WhatsappIntegrationInitialState.delete_chatbot
        },

        FetchChatBotRequesting: (state, action) => {
            state.fetch_chatbot.isLoading = true
        },
        FetchChatBotSuccess: (state, action) => {
            state.fetch_chatbot.isLoading = false
            state.fetch_chatbot.error = null
            state.fetch_chatbot.data = action.payload.data
        },
        FetchChatBotError: (state, action) => {
            state.fetch_chatbot.data = null
            state.fetch_chatbot.isLoading = false
            state.fetch_chatbot.error = action.payload
        },
        FetchChatBotReset: (state, action) => {
            state.fetch_chatbot = WhatsappIntegrationInitialState.fetch_chatbot
        },

        UpdateChatBotRequesting: (state, action) => {
            state.update_chatbot.isLoading = true
        },
        UpdateChatBotSuccess: (state, action) => {
            state.update_chatbot.isLoading = false
            state.update_chatbot.error = null
            state.update_chatbot.data = action.payload.data
        },
        UpdateChatBotError: (state, action) => {
            state.update_chatbot.data = null
            state.update_chatbot.isLoading = false
            state.update_chatbot.error = action.payload
        },
        UpdateChatBotReset: (state, action) => {
            state.update_chatbot = WhatsappIntegrationInitialState.update_chatbot
        },

        UpdateChatBotStatusRequesting: (state, action) => {
            state.update_chatbot_status.isLoading = true
        },
        UpdateChatBotStatusSuccess: (state, action) => {
            state.update_chatbot_status.isLoading = false
            state.update_chatbot_status.error = null
            state.update_chatbot_status.data = action.payload.data
        },
        UpdateChatBotStatusError: (state, action) => {
            state.update_chatbot_status.data = null
            state.update_chatbot_status.isLoading = false
            state.update_chatbot_status.error = action.payload
        },
        UpdateChatBotStatusReset: (state, action) => {
            state.update_chatbot_status = WhatsappIntegrationInitialState.update_chatbot_status
        },

        WhatsappChatListRequesting: (state, action) => {
            state.chat_list.isLoading = true
        },
        WhatsappChatInitListSuccess: (state, action) => {
            state.chat_list.data = action.payload.data;
            if (state.chat_list.data?.clients?.results) {
                state.chat_list.data.clients.results = deduplicateClientsByMobile(
                    [...state.chat_list.data.clients.results]
                );
            }
            state.chat_list.isLoading = false;
            state.chat_list.error = null;
        },
        WhatsappChatListSuccess: (state, action) => {
            const {chat_list} = current(state);
            const newData = action.payload.data;

            // Init if empty
            if (!state.chat_list.data) {
                state.chat_list.data = {
                    clients: {results: [], next: null},
                    enquiries: {results: [], next: null},
                };
            }

            // Merge clients
            const oldClientResults = chat_list?.data?.clients?.results || [];
            const newClientResults = newData?.clients?.results || [];

            const clientsUnique = deduplicateClientsByMobile(
                removeDuplicates([
                    ...oldClientResults,
                    ...newClientResults,
                ], "type_id")
            );

            state.chat_list.data.clients.results = clientsUnique;

            // Update next
            state.chat_list.data.clients.next = newData?.clients?.next || null;

            // Merge enquiries
            const oldEnquiryResults = chat_list?.data?.enquiries?.results || [];
            const newEnquiryResults = newData?.enquiries?.results || [];

            state.chat_list.data.enquiries.results = removeDuplicates([
                ...oldEnquiryResults,
                ...newEnquiryResults,
            ], "type_id");

            // Update next
            state.chat_list.data.enquiries.next = newData?.enquiries?.next || null;

            // Done
            state.chat_list.isLoading = false;
            state.chat_list.error = null;
        },
        WhatsappChatListError: (state, action) => {
            state.chat_list.data = null
            state.chat_list.isLoading = false
            state.chat_list.error = action.payload
        },
        WhatsappChatListReset: (state, action) => {
            state.chat_list = WhatsappIntegrationInitialState.chat_list
        },

        // Live session (human takeover) — start/end from chat header button
        WhatsappLiveSessionRequesting: (state) => {
            state.liveSession.isLoading = true;
            state.liveSession.error = null;
        },
        WhatsappLiveSessionSuccess: (state, action) => {
            state.liveSession.isLoading = false;
            state.liveSession.data = action.payload;
            state.liveSession.error = null;
        },
        WhatsappLiveSessionError: (state, action) => {
            state.liveSession.isLoading = false;
            state.liveSession.error = action.payload;
        },
        WhatsappLiveSessionReset: (state) => {
            state.liveSession = WhatsappIntegrationInitialState.liveSession;
        },

        // Bulk Batches Reducers
        GetBulkBatchesRequesting: (state, action) => {
            state.bulkBatches.isLoading = true;
        },
        GetBulkBatchesSuccess: (state, action) => {
            state.bulkBatches.isLoading = false;
            state.bulkBatches.error = null;
            state.bulkBatches.data = action.payload.data;
        },
        GetBulkBatchesError: (state, action) => {
            state.bulkBatches.data = null;
            state.bulkBatches.isLoading = false;
            state.bulkBatches.error = action.payload;
        },
        GetBulkBatchesReset: (state, action) => {
            state.bulkBatches = WhatsappIntegrationInitialState.bulkBatches;
        },

        // Scheduled bulk campaigns
        GetBulkSchedulesRequesting: (state) => {
            state.bulkSchedules.isLoading = true;
            state.bulkSchedules.error = null;
        },
        GetBulkSchedulesSuccess: (state, action) => {
            state.bulkSchedules.isLoading = false;
            state.bulkSchedules.error = null;
            state.bulkSchedules.data = action.payload;
        },
        GetBulkSchedulesError: (state, action) => {
            state.bulkSchedules.isLoading = false;
            state.bulkSchedules.error = action.payload;
            state.bulkSchedules.data = null;
        },
        GetBulkSchedulesReset: (state) => {
            state.bulkSchedules = WhatsappIntegrationInitialState.bulkSchedules;
        },
        GetBulkScheduleDetailRequesting: (state) => {
            state.bulkScheduleDetail.isLoading = true;
            state.bulkScheduleDetail.error = null;
        },
        GetBulkScheduleDetailSuccess: (state, action) => {
            state.bulkScheduleDetail.isLoading = false;
            state.bulkScheduleDetail.error = null;
            state.bulkScheduleDetail.data = action.payload;
        },
        GetBulkScheduleDetailError: (state, action) => {
            state.bulkScheduleDetail.isLoading = false;
            state.bulkScheduleDetail.error = action.payload;
        },
        GetBulkScheduleDetailReset: (state) => {
            state.bulkScheduleDetail = WhatsappIntegrationInitialState.bulkScheduleDetail;
        },
        BulkScheduleMutateRequesting: (state) => {
            state.bulkScheduleMutate.isLoading = true;
            state.bulkScheduleMutate.error = null;
        },
        BulkScheduleMutateSuccess: (state, action) => {
            state.bulkScheduleMutate.isLoading = false;
            state.bulkScheduleMutate.error = null;
            state.bulkScheduleMutate.data = action.payload;
        },
        BulkScheduleMutateError: (state, action) => {
            state.bulkScheduleMutate.isLoading = false;
            state.bulkScheduleMutate.error = action.payload;
        },
        BulkScheduleMutateReset: (state) => {
            state.bulkScheduleMutate = WhatsappIntegrationInitialState.bulkScheduleMutate;
        },

        // Bulk Batch Detail Reducers
        GetBulkBatchDetailRequesting: (state, action) => {
            state.bulkBatchDetail.isLoading = true;
        },
        GetBulkBatchDetailSuccess: (state, action) => {
            state.bulkBatchDetail.isLoading = false;
            state.bulkBatchDetail.error = null;
            state.bulkBatchDetail.data = action.payload.data;
        },
        GetBulkBatchDetailError: (state, action) => {
            state.bulkBatchDetail.data = null;
            state.bulkBatchDetail.isLoading = false;
            state.bulkBatchDetail.error = action.payload;
        },
        GetBulkBatchDetailReset: (state, action) => {
            state.bulkBatchDetail = WhatsappIntegrationInitialState.bulkBatchDetail;
        },

        // Bulk Batch Recipients Download
        DownloadBulkBatchRecipientsRequesting: (state, action) => {
            state.bulkBatchRecipientsDownload.isLoading = true;
        },
        DownloadBulkBatchRecipientsSuccess: (state, action) => {
            state.bulkBatchRecipientsDownload.isLoading = false;
            state.bulkBatchRecipientsDownload.error = null;
            state.bulkBatchRecipientsDownload.data = action.payload.data;
        },
        DownloadBulkBatchRecipientsError: (state, action) => {
            state.bulkBatchRecipientsDownload.data = null;
            state.bulkBatchRecipientsDownload.isLoading = false;
            state.bulkBatchRecipientsDownload.error = action.payload;
        },
        DownloadBulkBatchRecipientsReset: (state, action) => {
            state.bulkBatchRecipientsDownload = WhatsappIntegrationInitialState.bulkBatchRecipientsDownload;
        },

        UpdateBulkBatchStatusRequesting: (state, action) => {
            state.updateBulkBatchStatus.isLoading = true;
        },
        UpdateBulkBatchStatusSuccess: (state, action) => {
            state.updateBulkBatchStatus.isLoading = false;
            state.updateBulkBatchStatus.error = null;
            state.updateBulkBatchStatus.data = action.payload.data;
            
            // Update the bulk batches list if it exists
            if (state.bulkBatches.data?.results) {
                const batchIndex = state.bulkBatches.data.results.findIndex(
                    batch => batch.id === action.payload.batchId
                );
                if (batchIndex !== -1) {
                    state.bulkBatches.data.results[batchIndex] = {
                        ...state.bulkBatches.data.results[batchIndex],
                        status: action.payload.data.status
                    };
                }
            }
        },
        UpdateBulkBatchStatusError: (state, action) => {
            state.updateBulkBatchStatus.data = null;
            state.updateBulkBatchStatus.isLoading = false;
            state.updateBulkBatchStatus.error = action.payload;
        },
        UpdateBulkBatchStatusReset: (state, action) => {
            state.updateBulkBatchStatus = WhatsappIntegrationInitialState.updateBulkBatchStatus;
        },

        WhatsappChatListUpdateItem: (state, action) => {
            const {id, payload} = action.payload;
            const newType = payload?.type?.toLowerCase();

            if (!state.chat_list?.data) return;

            const isClient = newType === 'client' || newType === 'lead';

            // Ensure clients/enquiries containers exist before mutation
            if (!state.chat_list.data.clients) {
                state.chat_list.data.clients = {count: 0, next: null, previous: null, results: []};
            }
            if (!state.chat_list.data.enquiries) {
                state.chat_list.data.enquiries = {count: 0, next: null, previous: null, results: []};
            }

            const clientList = state.chat_list.data.clients?.results || [];
            const enquiryList = state.chat_list.data.enquiries?.results || [];

            const sortByLastMessage = (list) =>
                list.sort((a, b) => {
                    const dateA = a.last_message_on ? new Date(a.last_message_on) : new Date(0);
                    const dateB = b.last_message_on ? new Date(b.last_message_on) : new Date(0);
                    return dateB - dateA;
                });

            const displayName = (item) =>
                item?.name ||
                [item?.first_name, item?.last_name].filter(Boolean).join(" ").trim() ||
                "";

            const updateItemInList = (list) => {
                // Match by type_id, client id, or mobile (leads use lead id in list, upsert uses client id)
                const index = list.findIndex((item) => {
                    if (id != null && String(item.type_id) === String(id)) return true;
                    if (payload?.type_id != null && String(item.type_id) === String(payload.type_id)) return true;
                    if (payload?.mobile && item.mobile && String(item.mobile) === String(payload.mobile)) return true;
                    return false;
                });
                if (index !== -1) {
                    const existing = list[index];
                    const {_bumpUnread, first_name, last_name, ...rest} = payload || {};
                    let nextUnread = rest.unread;
                    if (_bumpUnread) {
                        nextUnread = (Number(existing.unread) || 0) + 1;
                    } else if (nextUnread == null) {
                        nextUnread = existing.unread;
                    }
                    list[index] = {
                        ...existing,
                        ...rest,
                        unread: nextUnread,
                        name: rest.name || existing.name || displayName({first_name, last_name}) || existing.name,
                    };
                    return true;
                }
                return false;
            };

            const updatedInClient = updateItemInList(clientList);
            const updatedInEnquiry = updateItemInList(enquiryList);

            if (!updatedInClient && !updatedInEnquiry) {
                const {_bumpUnread, first_name, last_name, ...rest} = payload || {};
                const seeded = {
                    ...rest,
                    type_id: rest.type_id || id,
                    name: rest.name || displayName({first_name, last_name}),
                    unread: _bumpUnread ? (Number(rest.unread) || 0) + 1 : (rest.unread ?? 1),
                };
                if (isClient) {
                    clientList.push(seeded);
                } else {
                    enquiryList.push(seeded);
                }
            }

            state.chat_list.data.clients.results = sortByLastMessage(clientList);
            state.chat_list.data.enquiries.results = sortByLastMessage(enquiryList);
        },

        WhatsappChatListDeleteItem: (state, action) => {
            const {id, type} = action.payload;
            const itemType = type?.toLowerCase();

            if (!state.chat_list?.data) return;

            const isClient = itemType === 'client' || itemType === 'lead';

            const clientList = state.chat_list.data.clients?.results || [];
            const enquiryList = state.chat_list.data.enquiries?.results || [];

            const removeFromList = (list) => {
                const index = list.findIndex(item => item.type_id === id);
                if (index !== -1) {
                    list.splice(index, 1);
                }
            };

            if (isClient) {
                removeFromList(clientList);
                state.chat_list.data.clients.results = clientList;
            } else {
                removeFromList(enquiryList);
                state.chat_list.data.enquiries.results = enquiryList;
            }
        },
        WhatsappResetState: () => WhatsappIntegrationInitialState
    }
})

export const listWhatsappMessageAPI = (query) => async dispatch => {
    dispatch(ListWhatsappMessageRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.listMessage, null, query), {cancelToken : query.cancelToken})
        .then((response) => {
            dispatch(ListWhatsappMessageSuccess(response.data))
        })
        .catch((error) => {
            if (axios.isCancel(error)) return;
            dispatch(ListWhatsappMessageError(formatAxiosErrorMessage(error)))});
}

export const listEnquiryWhatsappMessageAPI = (query) => async dispatch => {
    dispatch(ListWhatsappMessageRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.listEnquiryMessage, null, query), {})
        .then((response) => {
            dispatch(ListWhatsappMessageSuccess(response.data))
        })
        .catch((error) => dispatch(ListWhatsappMessageError(formatAxiosErrorMessage(error))));
}

export const searchWhatsappMessageAPI = (query) => async dispatch => {
    dispatch(ListWhatsappMessageRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.search, null, query), {})
        .then((response) => {
            // dispatch(ListWhatsappMessageSuccess(response.data))
            dispatch(SearchWhatsappMessageSuccess(response.data))
        })
        .catch((error) => dispatch(ListWhatsappMessageError(formatAxiosErrorMessage(error))));
}

export const listWhatsappTemplatesAPI = (query) => async dispatch => {
    dispatch(WhatsappTemplatesRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.templates, null, query), {})
        .then((response) => {
            dispatch(WhatsappTemplatesSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappTemplatesError(formatAxiosErrorMessage(error))));
}

export const createWhatsappTemplatesAPI = (data) => async dispatch => {
    dispatch(WhatsappCreateTemplateRequesting());
    apiService
        .post(apiUrlBuilder(endPoints.integration.templates, null, {}), data)
        .then((response) => {
            dispatch(WhatsappCreateTemplateSuccess(response.data))
        })
        .catch((error) => {
            // Extract proper error message from response
            let errorMessage = formatAxiosErrorMessage(error);
            if (error?.response?.data?.data?.error_user_msg) {
                errorMessage = error.response.data.data.error_user_msg;
            } else if (error?.response?.data?.data?.error_user_title) {
                errorMessage = error.response.data.data.error_user_title;
            } else if (error?.response?.data?.data?.message) {
                errorMessage = error.response.data.data.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            dispatch(WhatsappCreateTemplateError(errorMessage));
        });
}

export const deleteWhatsappTemplatesAPI = (urlData) => async dispatch => {
    dispatch(WhatsappDeleteTemplateRequesting());
    apiService
        .delete(apiUrlBuilder(endPoints.integration.templates, {}, urlData), {})
        .then((response) => {
            dispatch(WhatsappDeleteTemplateSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappDeleteTemplateError(formatAxiosErrorMessage(error))));
}

export const sendWhatsappMessageAPI = (query, data, options = {}) => async dispatch => {
    const feReferenceId = options.feReferenceId || null;
    dispatch(SendWhatsappMessageRequesting());
    // Ensure multipart FormData carries the optimistic correlation id
    if (feReferenceId && data instanceof FormData && !data.has("fe_reference_id")) {
        data.append("fe_reference_id", feReferenceId);
    }

    const postViaHttp = () =>
        apiService
            .post(apiUrlBuilder(endPoints.integration.listMessage, null, query), data)
            .then((response) => {
                dispatch(SendWhatsappMessageSuccess(response.data))
                // Reconcile optimistic bubble (id / fe_reference_id match)
                dispatch(WhatsappMessageIncoming(response.data))
            })
            .catch((error) => {
                if (feReferenceId) {
                    dispatch(WhatsappMessageOptimisticFailed({fe_reference_id: feReferenceId}));
                }
                dispatch(SendWhatsappMessageError(formatAxiosErrorMessage(error)));
            });

    // Socket-first only for plain text / reply (no attachment or template)
    const canTrySocket =
        data instanceof FormData &&
        feReferenceId &&
        query?.client &&
        data.get("type") === "text" &&
        !data.get("attachment") &&
        !data.get("template");

    if (!canTrySocket) {
        return postViaHttp();
    }

    try {
        const ack = await sendWhatsappMessageOverSocket({
            client_id: query.client,
            fe_reference_id: feReferenceId,
            type: "text",
            message_text: data.get("message_text") || "",
            ...(data.get("reply_id") ? {reply_id: data.get("reply_id")} : {}),
        });
        const message = ack?.message ?? ack;
        dispatch(SendWhatsappMessageSuccess(message));
        dispatch(WhatsappMessageIncoming(message));
    } catch (socketErr) {
        // Permission denied: do not double-send via HTTP
        if (socketErr?.fallback === false || socketErr?.code === "permission_denied") {
            if (feReferenceId) {
                dispatch(WhatsappMessageOptimisticFailed({fe_reference_id: feReferenceId}));
            }
            dispatch(
                SendWhatsappMessageError(
                    socketErr?.error || socketErr?.code || "Failed to send message"
                )
            );
            return;
        }
        // Socket down / timeout / send_failed → HTTP fallback
        return postViaHttp();
    }
}

export const WhatsappIntegratedAPI = () => async dispatch => {
    dispatch(WhatsappIntegratedRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.isWhatsappConfig, null, {}), {})
        .then((response) => {
            dispatch(WhatsappIntegratedSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappIntegratedError(formatAxiosErrorMessage(error))));
}

export const WhatsappConfigurationAPI = () => async dispatch => {
    dispatch(WhatsappConfigurationRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.createConfig, null, {}), {})
        .then((response) => {
            dispatch(WhatsappConfigurationSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappConfigurationError(formatAxiosErrorMessage(error))));
}

export const WhatsappAnalyticsAPI = (query) => async dispatch => {
    dispatch(WhatsappAnalyticsRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.whatsappAnalytics, null, query), {})
        .then((response) => {
            dispatch(WhatsappAnalyticsSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappAnalyticsError(formatAxiosErrorMessage(error))));
}

export const MessageStatAPI = (query) => async dispatch => {
    dispatch(MessageStatRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.messageStats, null, query), {})
        .then((response) => {
            dispatch(MessageStatSuccess(response.data))
        })
        .catch((error) => dispatch(MessageStatError(formatAxiosErrorMessage(error))));
}

export const WhatsappUnidentifiedSenderAPI = (query) => async dispatch => {
    dispatch(WhatsappUnidentifiedSenderRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.unidentifiedSender, null, query), {})
        .then((response) => {
            dispatch(WhatsappUnidentifiedSenderSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappUnidentifiedSenderError(formatAxiosErrorMessage(error))));
}

export const EmailUnidentifiedSenderAPI = (query) => async dispatch => {
    dispatch(EmailUnidentifiedSenderRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.unidentifiedSender, null, query), {})
        .then((response) => {
            dispatch(EmailUnidentifiedSenderSuccess(response.data))
        })
        .catch((error) => dispatch(EmailUnidentifiedSenderError(formatAxiosErrorMessage(error))));
}

export const DeleteWhatsappUnidentifiedSenderAPI = (urlData, query) => async dispatch => {
    dispatch(DeleteWhatsappUnidentifiedSenderRequesting());
    apiService
        .delete(apiUrlBuilder(endPoints.integration.deleteUnidentifiedSender, urlData, query), {})
        .then((response) => {
            dispatch(DeleteWhatsappUnidentifiedSenderSuccess(response.data))
        })
        .catch((error) => dispatch(DeleteWhatsappUnidentifiedSenderError(formatAxiosErrorMessage(error))));
}

export const WhatsappCreateConfigurationAPI = (data) => async dispatch => {
    dispatch(WhatsappCreateConfigurationRequesting());
    apiService
        .post(apiUrlBuilder(endPoints.integration.createConfig, null, {}), data)
        .then((response) => {
            dispatch(WhatsappCreateConfigurationSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappCreateConfigurationError(formatAxiosErrorMessage(error))));
}
export const WhatsappDeleteConfigurationAPI = (id, force = false) => async dispatch => {
    dispatch(WhatsappDeleteConfigurationRequesting());
    apiService
        .delete(apiUrlBuilder(endPoints.integration.createConfig + `${id}/`, null, {force: force}), {})
        .then((response) => {
            dispatch(WhatsappDeleteConfigurationSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappDeleteConfigurationError(formatAxiosErrorMessage(error))));
}

export const WhatsappUpdateConfigurationAPI = (data) => async dispatch => {
    dispatch(WhatsappUpdateConfigurationRequesting());
    apiService
        .patch(apiUrlBuilder(endPoints.integration.whatsappPhoneNumberConfirmation, null, {}), data)
        .then((response) => {
            dispatch(WhatsappUpdateConfigurationSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappUpdateConfigurationError(formatAxiosErrorMessage(error))));
}

export const WhatsappBulkMessageAPI = (data) => async dispatch => {
    dispatch(WhatsappBulkRequesting());
    apiService
        .post(apiUrlBuilder(endPoints.integration.bulkWhatsapp, null, {}), data)
        .then((response) => {
            dispatch(WhatsappBulkSuccess({data: "success bulk"}))
        })
        .catch((error) => dispatch(WhatsappBulkError(formatAxiosErrorMessage(error))));
}

export const WhatsappBulkStatusAPI = (data) => async dispatch => {
    dispatch(WhatsappBulkStatusRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.bulkWhatsappStatus, null, {}), data)
        .then((response) => {
            dispatch(WhatsappBulkStatusSuccess(response.data))
        })
        .catch((error) => dispatch(WhatsappBulkStatusError(formatAxiosErrorMessage(error))));
}


export const CreateChatBotAPI = (data) => async dispatch => {
    dispatch(CreateChatBotRequesting());
    apiService
        .post(apiUrlBuilder(endPoints.integration.chatbot.config, null, {}), data)
        .then((response) => {
            dispatch(CreateChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(CreateChatBotError(formatAxiosErrorMessage(error))));
}

export const ListChatbotAPI = (data) => async dispatch => {
    dispatch(ListChatBotRequesting());
    // console.log(data)
    apiService
        .get(apiUrlBuilder(endPoints.integration.chatbot.config, null, data), data)
        .then((response) => {
            dispatch(ListChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(ListChatBotError(formatAxiosErrorMessage(error))));
}

export const ListAllChatbotAPI = () => async dispatch => {
    dispatch(ListAllChatBotRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.chatbot.config, null, {no_page: "true"}), {})
        .then((response) => {
            dispatch(ListAllChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(ListAllChatBotError(formatAxiosErrorMessage(error))));
}

export const ChatbotDeleteAPI = (id) => async dispatch => {
    dispatch(DeleteChatBotRequesting());
    apiService
        .delete(apiUrlBuilder(endPoints.integration.chatbot.config + `${id}/`, null, {}), {})
        .then((response) => {
            dispatch(DeleteChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(DeleteChatBotError(formatAxiosErrorMessage(error))));
}

export const FetchChatbotAPI = (id) => async dispatch => {
    dispatch(FetchChatBotRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.chatbot.config + `${id}/`, null, {}), {})
        .then((response) => {
            dispatch(FetchChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(FetchChatBotError(formatAxiosErrorMessage(error))));
}


export const UpdateChatBotAPI = (id, data) => async dispatch => {
    dispatch(UpdateChatBotRequesting());
    apiService
        .patch(endPoints.integration.chatbot.config + id + "/", data)
        .then((response) => {
            dispatch(UpdateChatBotSuccess(response.data))
        })
        .catch((error) => dispatch(UpdateChatBotError(formatAxiosErrorMessage(error))));
}

export const UpdateChatBotStatusAPI = (data) => async dispatch => {
    dispatch(UpdateChatBotStatusRequesting());
    apiService
        .patch(endPoints.integration.chatbot.update_status, data)
        .then((response) => {
            dispatch(UpdateChatBotStatusSuccess(response.data))
        })
        .catch((error) => dispatch(UpdateChatBotStatusError(formatAxiosErrorMessage(error))));
}

export const FetchChatListAPI = (queryData = {}, isInitCall = false) => async dispatch => {
    dispatch(WhatsappChatListRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.chatList, null, queryData), {})
        .then((response) => {
            if (isInitCall) {
                dispatch(WhatsappChatInitListSuccess(response.data))
            } else {
                dispatch(WhatsappChatListSuccess(response.data))
            }
        })
        .catch((error) => dispatch(WhatsappChatListError(formatAxiosErrorMessage(error))));
}

/** Start or end agent takeover for a WhatsApp thread (pauses chatbot + AI while active). */
export const WhatsappLiveSessionAPI = ({type, type_id, active}) => async dispatch => {
    dispatch(WhatsappLiveSessionRequesting());
    return apiService
        .post(endPoints.integration.liveSession, {type, type_id, active})
        .then((response) => {
            const data = response.data?.data || response.data || {};
            dispatch(WhatsappLiveSessionSuccess(data));
            // Keep chat-list row in sync so the header button flips immediately
            if (type_id != null) {
                dispatch(WhatsappChatListUpdateItem({
                    id: type_id,
                    payload: {
                        type_id,
                        type: data.type || type,
                        is_human_takeover_active: !!data.is_human_takeover_active,
                        ...(data.mobile ? {mobile: data.mobile} : {}),
                    },
                }));
            }
            return data;
        })
        .catch((error) => {
            dispatch(WhatsappLiveSessionError(formatAxiosErrorMessage(error)));
            throw error;
        });
}

export const GetBulkBatchesApi = (query = '') => async dispatch => {
    dispatch(GetBulkBatchesRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.bulkBatches, null, query), {})
        .then((response) => {
            dispatch(GetBulkBatchesSuccess(response.data))
        })
        .catch((error) => dispatch(GetBulkBatchesError(formatAxiosErrorMessage(error))));
}

export const GetBulkBatchDetailApi = (batchId) => async dispatch => {
    dispatch(GetBulkBatchDetailRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.bulkBatchDetail, {id: batchId}, {}))
        .then((response) => {
            dispatch(GetBulkBatchDetailSuccess(response.data))
        })
        .catch((error) => dispatch(GetBulkBatchDetailError(formatAxiosErrorMessage(error))));
}

export const DownloadBulkBatchRecipientsApi = (batchId) => async dispatch => {
    dispatch(DownloadBulkBatchRecipientsRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.bulkBatchRecipients, {id: batchId}, {}), {
            responseType: 'blob',
            "Access-Control-Expose-Headers": '*',
        })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `whatsapp_campaign_${batchId}_recipients.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Cleanup the URL object
            window.URL.revokeObjectURL(url);
            dispatch(DownloadBulkBatchRecipientsSuccess({ data: 'Downloaded successfully' }));
        })
        .catch((error) => {
            dispatch(DownloadBulkBatchRecipientsError(formatAxiosErrorMessage(error)));
        });
}

export const UpdateBulkBatchStatusApi = (batchId, status) => async dispatch => {
    dispatch(UpdateBulkBatchStatusRequesting());
    apiService
        .patch(apiUrlBuilder(endPoints.integration.bulkBatchDetail, {id: batchId}, {}), {
            status: status
        })
        .then((response) => {
            dispatch(UpdateBulkBatchStatusSuccess({
                data: response.data,
                batchId: batchId
            }));
        })
        .catch((error) => dispatch(UpdateBulkBatchStatusError(formatAxiosErrorMessage(error))));
}

const unwrapSchedulePayload = (body) => {
    if (Array.isArray(body)) return body;
    if (body?.data != null) return body.data;
    return body;
};

export const GetBulkSchedulesApi = (query = {}) => async (dispatch) => {
    dispatch(GetBulkSchedulesRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.integration.bulkSchedules, null, query), {})
        .then((response) => {
            dispatch(GetBulkSchedulesSuccess(unwrapSchedulePayload(response.data)));
        })
        .catch((error) =>
            dispatch(GetBulkSchedulesError(formatAxiosErrorMessage(error)))
        );
};

export const GetBulkScheduleDetailApi = (scheduleId) => async (dispatch) => {
    dispatch(GetBulkScheduleDetailRequesting());
    apiService
        .get(
            apiUrlBuilder(endPoints.integration.bulkScheduleDetail, {id: scheduleId}, {}),
            {}
        )
        .then((response) => {
            dispatch(GetBulkScheduleDetailSuccess(unwrapSchedulePayload(response.data)));
        })
        .catch((error) =>
            dispatch(GetBulkScheduleDetailError(formatAxiosErrorMessage(error)))
        );
};

/** Create a scheduled / recurring WhatsApp bulk campaign (FormData). */
export const CreateWhatsappBulkScheduleAPI = (data) => async (dispatch) => {
    dispatch(BulkScheduleMutateRequesting());
    return apiService
        .post(apiUrlBuilder(endPoints.integration.bulkSchedules, null, {}), data)
        .then((response) => {
            const payload = unwrapSchedulePayload(response.data);
            dispatch(BulkScheduleMutateSuccess(payload));
            return payload;
        })
        .catch((error) => {
            const msg = formatAxiosErrorMessage(error);
            dispatch(BulkScheduleMutateError(msg));
            throw error;
        });
};

/** Pause / resume / cancel an entire schedule series. */
export const UpdateBulkScheduleApi = (scheduleId, action) => async (dispatch) => {
    dispatch(BulkScheduleMutateRequesting());
    return apiService
        .patch(
            apiUrlBuilder(endPoints.integration.bulkScheduleDetail, {id: scheduleId}, {}),
            {action}
        )
        .then((response) => {
            const payload = unwrapSchedulePayload(response.data);
            dispatch(BulkScheduleMutateSuccess(payload));
            return payload;
        })
        .catch((error) => {
            const msg = formatAxiosErrorMessage(error);
            dispatch(BulkScheduleMutateError(msg));
            throw error;
        });
};

/** Cancel a single pending occurrence in a series. */
export const CancelBulkScheduleOccurrenceApi = (scheduleId, occurrenceId) => async (dispatch) => {
    dispatch(BulkScheduleMutateRequesting());
    return apiService
        .post(
            apiUrlBuilder(endPoints.integration.bulkScheduleOccurrenceCancel, {
                id: scheduleId,
                occurrence_id: occurrenceId,
            }, {}),
            {}
        )
        .then((response) => {
            const payload = unwrapSchedulePayload(response.data);
            dispatch(BulkScheduleMutateSuccess(payload));
            return payload;
        })
        .catch((error) => {
            const msg = formatAxiosErrorMessage(error);
            dispatch(BulkScheduleMutateError(msg));
            throw error;
        });
};

export const {
    ListWhatsappMessageRequesting,
    ListWhatsappMessageSuccess,
    ListWhatsappMessageReset,
    ListWhatsappMessageError,

    SendWhatsappMessageRequesting,
    SendWhatsappMessageSuccess,
    SendWhatsappMessageReset,
    SendWhatsappMessageError,

    WhatsappTemplatesRequesting,
    WhatsappTemplatesSuccess,
    WhatsappTemplatesError,
    WhatsappTemplatesReset,
    SearchWhatsappMessageSuccess,
    SearchWhatsappMessageReset,

    WhatsappIntegratedRequesting,
    WhatsappIntegratedSuccess,
    WhatsappIntegratedError,
    WhatsappIntegratedReset,

    WhatsappConfigurationRequesting,
    WhatsappConfigurationSuccess,
    WhatsappConfigurationError,
    WhatsappConfigurationReset,

    WhatsappCreateConfigurationRequesting,
    WhatsappCreateConfigurationSuccess,
    WhatsappCreateConfigurationError,
    WhatsappCreateConfigurationReset,

    WhatsappDeleteConfigurationRequesting,
    WhatsappDeleteConfigurationSuccess,
    WhatsappDeleteConfigurationError,
    WhatsappDeleteConfigurationReset,

    WhatsappUpdateConfigurationRequesting,
    WhatsappUpdateConfigurationSuccess,
    WhatsappUpdateConfigurationError,
    WhatsappUpdateConfigurationReset,
    WhatsappMessageIncoming,
    WhatsappMessageOptimistic,
    WhatsappMessageOptimisticFailed,
    SetActiveWhatsappChat,
    ClearActiveWhatsappChat,

    WhatsappAnalyticsRequesting,
    WhatsappAnalyticsSuccess,
    WhatsappAnalyticsError,
    WhatsappAnalyticsReset,

    MessageStatRequesting,
    MessageStatSuccess,
    MessageStatError,
    MessageStatReset,

    WhatsappBulkRequesting,
    WhatsappBulkSuccess,
    WhatsappBulkError,
    WhatsappBulkReset,

    GetBulkSchedulesRequesting,
    GetBulkSchedulesSuccess,
    GetBulkSchedulesError,
    GetBulkSchedulesReset,
    GetBulkScheduleDetailRequesting,
    GetBulkScheduleDetailSuccess,
    GetBulkScheduleDetailError,
    GetBulkScheduleDetailReset,
    BulkScheduleMutateRequesting,
    BulkScheduleMutateSuccess,
    BulkScheduleMutateError,
    BulkScheduleMutateReset,

    WhatsappUnidentifiedSenderRequesting,
    WhatsappUnidentifiedSenderSuccess,
    WhatsappUnidentifiedSenderError,
    WhatsappUnidentifiedSenderReset,
    WhatsappUnidentifiedSenderMarkASRead,

    DeleteWhatsappUnidentifiedSenderRequesting,
    DeleteWhatsappUnidentifiedSenderSuccess,
    DeleteWhatsappUnidentifiedSenderError,
    DeleteWhatsappUnidentifiedSenderReset,

    WhatsappCreateTemplateRequesting,
    WhatsappCreateTemplateSuccess,
    WhatsappCreateTemplateError,
    WhatsappCreateTemplateReset,

    WhatsappDeleteTemplateRequesting,
    WhatsappDeleteTemplateSuccess,
    WhatsappDeleteTemplateError,
    WhatsappDeleteTemplateReset,

    WhatsappBulkStatusRequesting,
    WhatsappBulkStatusSuccess,
    WhatsappBulkStatusReset,
    WhatsappBulkStatusError,

    EmailUnidentifiedSenderRequesting,
    EmailUnidentifiedSenderSuccess,
    EmailUnidentifiedSenderReset,
    EmailUnidentifiedSenderError,
    EmailUnidentifiedSenderMarkASRead,


    CreateChatBotRequesting,
    CreateChatBotSuccess,
    CreateChatBotError,
    CreateChatBotReset,


    ListChatBotRequesting,
    ListChatBotSuccess,
    ListChatBotError,
    ListChatBotReset,

    ListAllChatBotRequesting,
    ListAllChatBotSuccess,
    ListAllChatBotError,

    DeleteChatBotRequesting,
    DeleteChatBotSuccess,
    DeleteChatBotError,
    DeleteChatBotReset,

    FetchChatBotRequesting,
    FetchChatBotSuccess,
    FetchChatBotError,
    FetchChatBotReset,

    UpdateChatBotRequesting,
    UpdateChatBotSuccess,
    UpdateChatBotError,
    UpdateChatBotReset,


    UpdateChatBotStatusRequesting,
    UpdateChatBotStatusSuccess,
    UpdateChatBotStatusError,
    UpdateChatBotStatusReset,

    WhatsappChatListRequesting,
    WhatsappChatListSuccess,
    WhatsappChatInitListSuccess,
    WhatsappChatListError,
    WhatsappChatListReset,

    WhatsappChatListUpdateItem,

    WhatsappLiveSessionRequesting,
    WhatsappLiveSessionSuccess,
    WhatsappLiveSessionError,
    WhatsappLiveSessionReset,

    // Bulk Batches Actions
    GetBulkBatchesRequesting,
    GetBulkBatchesSuccess,
    GetBulkBatchesError,
    GetBulkBatchesReset,

    // Bulk Batch Detail Actions
    GetBulkBatchDetailRequesting,
    GetBulkBatchDetailSuccess,
    GetBulkBatchDetailError,
    GetBulkBatchDetailReset,

    // Bulk Batch Recipients Download Actions
    DownloadBulkBatchRecipientsRequesting,
    DownloadBulkBatchRecipientsSuccess,
    DownloadBulkBatchRecipientsError,
    DownloadBulkBatchRecipientsReset,

    // Update Bulk Batch Status Actions
    UpdateBulkBatchStatusRequesting,
    UpdateBulkBatchStatusSuccess,
    UpdateBulkBatchStatusError,
    UpdateBulkBatchStatusReset,

    WhatsappChatListDeleteItem,
    WhatsappResetState

} = WhatsappSlice.actions;
export const WhatsappState = (state) => state.whatsapp
export default WhatsappSlice.reducer